import React, { useEffect } from 'react';
import { Appointment } from '../../../../types/appointments';
import { UserFormValues } from '../../../../types/users';
import { AppointmentCard } from '../../../../components/views-components/staff/appointments/AppointmentCard';
import { FormBody } from '../../../../components/common/Forms/Forms.styled';
import { StaffOrPatientFormData } from '../../../../components/views-components/staff/settings/users/StaffOrPatientFormData';
import { useForm } from 'react-hook-form';
import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';
import {
  BookAppointmentForm,
  EditAppointmentData,
} from '../../../../components/views-components/staff/appointments/EditAppointmentData';
import { DuplicatePatientDialog } from './DuplicatePatientDialog';
import { ButtonSetWithCloseButton } from '../../../../components/Form/ButtonSetWithCloseButton';
import { CommonButton as Button } from '../../../../components/common/Forms/Button';
import { FaUserClock } from 'react-icons/fa';
import { useStaffAppointmentBookNoPatient } from './useStaffAppointmentBookNoPatient';
import { useSite } from '../../../../context/SiteContext';
import { CardSubtitle } from '../../../../components/common';
import { useSelectAppointmentToBook } from '../common/useSelectAppointmentToBook';
import { BookAppointmentOnlineStaffOnlySwitchSection } from '../common/BookAppointmentOnlineStaffOnlySwitchSection';
import { useStaffAppointmentBooking } from '.././common/appointmentFormHooks';

interface IAppointmentFormProps {
  closeForm: () => void;
  selectedSlotAppointments: Appointment[];
}

type AppointmentWithUser = {
  user: UserFormValues;
  appointment: BookAppointmentForm;
};

export const BookAppointmentNoPatient: React.FunctionComponent<
  IAppointmentFormProps
> = ({ selectedSlotAppointments, closeForm }) => {
  const { t } = useCommonTranslation();
  const { activeSite } = useSite();

  const { selectedAppointment, ...onlineSwitchProps } =
    useSelectAppointmentToBook({
      selectedSlotAppointments,
      onError: closeForm,
    });

  const { bookPatientAppointment, isLoading: isApptBookOrLockLoading } =
    useStaffAppointmentBooking({
      closeForm,
      selectedAppointment,
    });

  const {
    createAndBookPatientWithValidation,
    duplicateResolveSelect,
    duplicateResolveCreateNew,
    duplicateUsers,
    isLoading: isStaffBookingLoading,
    surgeryOptions,
    referrerOptions,
  } = useStaffAppointmentBookNoPatient({
    bookPatientAppointment,
  });

  const {
    register,
    control,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<AppointmentWithUser>();

  useEffect(() => {
    setFocus('user.firstname');
  }, [setFocus]);

  const isLoading = isApptBookOrLockLoading || isStaffBookingLoading;

  return (
    <>
      <AppointmentCard
        isLoading={isLoading}
        title={t('appointment-detail-title')}
        onCloseClicked={closeForm}
        icon={<FaUserClock />}
      >
        <BookAppointmentOnlineStaffOnlySwitchSection {...onlineSwitchProps} />
        <FormBody>
          <CardSubtitle>
            {t('appointment-book-patient-information')}
          </CardSubtitle>
          <StaffOrPatientFormData
            gpOptions={surgeryOptions}
            register={register}
            control={control}
            errors={errors}
            siteSettings={activeSite}
          />
          <CardSubtitle>{t('appointment-detail-information')}</CardSubtitle>
          <EditAppointmentData
            register={register}
            control={control}
            errors={errors}
            referrers={referrerOptions}
            selectedAppointment={selectedAppointment}
          />
          {duplicateUsers && (
            <DuplicatePatientDialog
              duplicateUsers={duplicateUsers}
              onCreateNewPatientSelected={duplicateResolveCreateNew}
              onPatientSelected={async (patient) => {
                await duplicateResolveSelect({ selectedPatient: patient });
              }}
            />
          )}
          <ButtonSetWithCloseButton onCloseClicked={closeForm}>
            <Button
              variant="primary"
              type={'button'}
              onClick={async () => {
                await handleSubmit((data) => {
                  createAndBookPatientWithValidation({
                    queue: false,
                    appointment: data.appointment,
                    user: data.user,
                  });
                })();
              }}
            >
              {t('save')}
            </Button>
          </ButtonSetWithCloseButton>
        </FormBody>
      </AppointmentCard>
    </>
  );
};
